import type { Quote, Signal, TechnicalSnapshot, DayTradeScore, IntradayRange } from '@invest-copilot/shared';

interface SignalResp {
  snapshot: TechnicalSnapshot;
  signal: Signal;
  dayTrade?: DayTradeScore;
  intradayRange?: IntradayRange;
}

/**
 * 將後端 Signal 與 TWSE 即時報價結合：
 * - %B (Bollinger 位置)：用即時 price + 昨日帶寬 → 重算
 * - IntradayRange：basePrice 換成即時 price，估算高低重算
 * - snapshot.price：換成即時
 * - 加上 `liveOverlay = true` 旗標讓 UI 顯示「⚡ 即時」
 *
 * MA / RSI / MACD / Stochastic 等滾動指標仍用後端值（變動慢，盤中不會差很多）
 */
export function enhanceSignalWithLivePrice(
  data: SignalResp | undefined,
  enhancedQuote: Quote | undefined,
): (SignalResp & { liveOverlay?: boolean; livePrice?: number }) | undefined {
  if (!data) return undefined;
  // 只有 TWSE 即時源才覆蓋；yahoo 本身的台股有延遲，但也不會比後端 signal 更新
  if (!enhancedQuote || enhancedQuote.dataSource !== 'twse') return data;

  const livePrice = enhancedQuote.price;
  const { snapshot, intradayRange } = data;

  // 重算 %B
  let newBollinger = snapshot.bollinger;
  if (snapshot.bollinger) {
    const { upper, lower } = snapshot.bollinger;
    const span = upper - lower;
    if (span > 0) {
      newBollinger = {
        ...snapshot.bollinger,
        percentB: (livePrice - lower) / span,
      };
    }
  }

  // 重算 IntradayRange
  let newRange = intradayRange;
  if (intradayRange?.atr14) {
    const k = 0.7;
    const high = livePrice + intradayRange.atr14 * k;
    const low = livePrice - intradayRange.atr14 * k;
    const pct = ((intradayRange.atr14 * k * 2) / livePrice) * 100;
    newRange = {
      ...intradayRange,
      basePrice: livePrice,
      estimatedHigh: Number(high.toFixed(2)),
      estimatedLow: Number(low.toFixed(2)),
      estimatedRangePct: Number(pct.toFixed(2)),
      note: `以即時價 ${livePrice.toFixed(2)} 為基準，過去 14 日平均波動推算今日約 70% 機率落在 ${low.toFixed(2)} ~ ${high.toFixed(2)}。突破上下緣代表有大事。`,
    };
  }

  return {
    ...data,
    snapshot: {
      ...snapshot,
      price: livePrice,
      bollinger: newBollinger,
    },
    intradayRange: newRange,
    liveOverlay: true,
    livePrice,
  };
}
